import { useTranslation } from "react-i18next";
import type { UserSummary } from "~/api/types";
import { extractOwners } from "~/helpers/workflow";
import { useFilter, type Filter } from "~/hooks/useFilter";
import { useUserContext } from "~/hooks/useUserContext";
import { type ApplyTemplateRow } from "~/hooks/useApplyTemplateRows";
import type { Option } from "~/molecules/ControlledCombobox";

const NO_TEMPLATE = "none";

const collator = new Intl.Collator(undefined, { numeric: true });

const sortedOptions = (labels: Map<string, string>): Option[] =>
  [...labels]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => collator.compare(a.label, b.label));

interface Params {
  rows: ApplyTemplateRow[];
  tagMap: Map<number, string>;
}

export const useWorkflowTemplateFilters = ({ rows, tagMap }: Params) => {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();

  // One pass over the rows collects every option label; the filters below
  // only read from these.
  const templateLabels = new Map<string, string>([
    [NO_TEMPLATE, t(($) => $.workflowTemplates.documentsTable.none)],
  ]);
  const tierLabels = new Map<string, string>();
  const categoryLabels = new Map<string, string>();
  const tagLabels = new Map<string, string>();
  const owners: UserSummary[] = [];

  for (const row of rows) {
    if (row.currentTemplateId != null) {
      templateLabels.set(
        String(row.currentTemplateId),
        row.currentTemplateName ?? t(($) => $.workflowTemplates.defaultBadge),
      );
    }
    if (row.tier != null) tierLabels.set(row.tier, row.tier);
    if (row.category != null) {
      categoryLabels.set(String(row.category.id), row.category.title);
    }
    owners.push(row.owner);
    for (const tagId of row.tagIds) {
      tagLabels.set(String(tagId), tagMap.get(tagId) ?? String(tagId));
    }
  }

  const filterInput: Filter<ApplyTemplateRow>[] = [
    {
      id: "template",
      label: t(($) => $.workflowTemplates.documentsTable.currentTemplate),
      isMulti: true,
      options: sortedOptions(templateLabels),
      getValue: (row) =>
        row.currentTemplateId != null
          ? String(row.currentTemplateId)
          : NO_TEMPLATE,
    },
    {
      id: "tier",
      label: t(($) => $.commons.document.tier),
      isMulti: true,
      options: sortedOptions(tierLabels),
      getValue: (row) => row.tier ?? [],
    },
    {
      id: "category",
      label: t(($) => $.commons.document.category),
      isMulti: true,
      options: sortedOptions(categoryLabels),
      getValue: (row) => (row.category ? String(row.category.id) : []),
    },
    {
      id: "tag",
      label: t(($) => $.commons.document.tag),
      isMulti: true,
      options: sortedOptions(tagLabels),
      getValue: (row) => row.tagIds.map(String),
    },
    {
      id: "owner",
      label: t(($) => $.commons.document.owner),
      isMulti: true,
      // Shared helper so "(you)" and current-user-first ordering match the
      // owner filter everywhere else in the app.
      options: extractOwners(owners, currentUser.id),
      getValue: (row) => String(row.owner.userId),
    },
  ];

  return useFilter(filterInput);
};
